'use client'

import { Check, ChevronDown, Plus, Search, Sparkles, X } from 'lucide-react'
import Link from 'next/link'
import { useMemo, useState } from 'react'

import { ChoiceChip } from '@/components/molecules/choice-chip.client'
import { brandTone } from '@/lib/brand-tones'
import { type CreatorOfferingView, type CreatorPick, defaultQtyFor } from '@/lib/creator'
import {
  availableQuickStarts,
  catalogueGroups,
  fromPrice,
  matchesQuery,
  quickPick,
} from '@/lib/creator-quick'
import { PICKED_FILL, RADIUS } from '@/lib/surface'
import { KICKER, META, MICRO } from '@/lib/typography'
import { cn } from '@/lib/utils'

/** Where focus goes when the last position is removed — see `OrderLineList`. */
export const CATALOGUE_SEARCH_ID = 'szukaj-w-ofercie'

/**
 * Everything else we sell, as something you can actually look through.
 *
 * ## What this replaces
 *
 * Three components and two different ideas. `OrderPicker` was a `<select>`
 * holding the WHOLE catalogue — twenty-five rows grouped by page, no prices,
 * no search, and the only route to anything that was not featured: ordering a
 * Sony A7 III meant opening a dropdown and reading it to the end.
 * `OrderSuggestions` and `OrderSuggestionCard` were the other half, a separate
 * band below it showing three rows the panel had flagged, each one a full-width
 * dashed card with its own heading and its own variant chips.
 *
 * So the screen offered the same action — put something in the order — in two
 * places, in two shapes, one of which could reach three things and the other
 * everything. The owner's words for the result: *„sporo wybierania z listy"*.
 *
 * ## What it does instead
 *
 * One panel. A search box that tolerates Polish typed without diacritics, the
 * pages as filter chips with counts, and the offer as a grid of tiles carrying
 * a name and an entry price. **One click adds**, at the recommended level —
 * see `quickPick`, and the note there about why it does not also guess a date.
 *
 * Anyone who wants a specific level opens the tile's own chips; that is one
 * extra click rather than a hunt, and it is the same `ChoiceChip` the line
 * cards use.
 *
 * ## It still writes nothing until somebody clicks
 *
 * The invariant the old suggestion card was built around survives, because it
 * is the reason this screen can be opened by a crawler: **a page view creates
 * no row.** A tile holds no state, cannot be edited, and is visibly not a
 * position — dashed frame, its own heading, and a sentence saying so. The
 * first deliberate act is the add.
 *
 * ## Quick starts
 *
 * Three whole baskets, and they only appear on an EMPTY order — a shortcut for
 * starting something is noise on top of something already started. They fill
 * in the picking and nothing else: every line they create still asks for its
 * brief and its dates on its own card.
 */
export function OrderCatalogue({
  offerings,
  taken,
  linked,
  hasLines,
  onAdd,
}: {
  offerings: CreatorOfferingView[]
  /** Slugs already in the order. Shown as such rather than hidden — see below. */
  taken: ReadonlySet<string>
  /** What a deep link asked for, pinned first and pre-pointed at its variant. */
  linked?: { offering: CreatorOfferingView; pick: CreatorPick }
  hasLines: boolean
  onAdd: (offeringSlug: string, pick: CreatorPick) => void
}) {
  const [query, setQuery] = useState('')
  const [page, setPage] = useState<string | null>(null)
  /** Which tile has its levels open. One at a time; the grid stays a grid. */
  const [expanded, setExpanded] = useState<string | null>(null)

  const groups = useMemo(() => catalogueGroups(offerings), [offerings])
  const quickStarts = useMemo(() => availableQuickStarts(offerings), [offerings])

  const shown = useMemo(() => {
    const matched = offerings.filter(
      (o) => (page === null || o.pageSlug === page) && matchesQuery(o, query),
    )
    // Whatever a link named first, then the panel's featured rows, then the
    // rest in catalogue order. Somebody who pressed "Wybierz Creator" on the
    // montaż page came here for exactly that and must not have to look for it.
    return [...matched].sort((a, b) => {
      const rank = (o: CreatorOfferingView) =>
        o.slug === linked?.offering.slug ? 0 : o.featured ? 1 : 2
      return rank(a) - rank(b)
    })
  }, [offerings, page, query, linked])

  if (offerings.length === 0) return null

  return (
    <section
      aria-labelledby="oferta-tytul"
      className={cn(
        'border border-dashed border-white/15 bg-white/[0.02] p-5 sm:p-6',
        RADIUS.feature,
      )}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h2 id="oferta-tytul" className="text-[1.05rem] font-bold text-white">
          {/* "Dorzuć" only makes sense on top of something. An empty order is
              not being added to, it is being started. */}
          {hasLines ? 'Dorzuć do zamówienia' : 'Zacznij zamówienie'}
        </h2>
        <p className={cn(META, 'text-white/50')}>
          Nic z tego nie jest jeszcze w zamówieniu — dodaje je dopiero kliknięcie.
        </p>
      </div>

      {/* ── QUICK STARTS ──────────────────────────────────── */}
      {!hasLines && quickStarts.length > 0 ? (
        <div className="mt-5">
          <p className={cn(KICKER, 'flex items-center gap-2 text-white/45')}>
            <Sparkles size={12} aria-hidden="true" />
            Szybki start
          </p>
          <ul className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
            {quickStarts.map(({ start, lines }, i) => {
              const tone = brandTone(i * 3)
              return (
                <li key={start.id}>
                  <button
                    type="button"
                    onClick={() => {
                      for (const line of lines) onAdd(line.offering.slug, line.pick)
                    }}
                    className={cn(
                      'group relative flex h-full w-full flex-col gap-1.5 overflow-hidden border border-white/12 bg-white/[0.05] p-4 text-left transition-colors duration-200 hover:border-white/30 hover:bg-white/[0.09]',
                      RADIUS.box,
                    )}
                  >
                    <span
                      aria-hidden="true"
                      className="pointer-events-none absolute inset-0"
                      style={{
                        background: `radial-gradient(90% 120% at 10% 0%, color-mix(in oklab, ${tone} 16%, transparent), transparent 68%)`,
                      }}
                    />
                    <span className="relative flex items-center gap-2 text-[0.95rem] font-bold text-white">
                      {start.label}
                    </span>
                    <span className={cn(META, 'relative text-white/60')}>{start.blurb}</span>
                    {/* What it will actually put in the basket, counted. A
                        shortcut that does not say what it does is a button
                        nobody presses twice. */}
                    <span
                      className={cn(MICRO, 'relative mt-1 font-semibold')}
                      style={{ color: tone }}
                    >
                      {lines.map((line) => `${line.pick.qty}× ${line.offering.name}`).join(' + ')}
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
        </div>
      ) : null}

      {/* ── SEARCH ────────────────────────────────────────── */}
      <div className="relative mt-5">
        <Search
          size={15}
          aria-hidden="true"
          className="pointer-events-none absolute top-1/2 left-4 -translate-y-1/2 text-white/40"
        />
        <input
          id={CATALOGUE_SEARCH_ID}
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Szukaj: miniaturka, kamera, studio…"
          aria-label="Szukaj w ofercie"
          className={cn(
            'h-12 w-full border border-white/15 bg-black/40 pr-11 pl-11 text-[0.95rem] text-white placeholder:text-white/40',
            RADIUS.pill,
            'transition-colors outline-none focus:border-white/40',
          )}
        />
        {query ? (
          <button
            type="button"
            onClick={() => setQuery('')}
            aria-label="Wyczyść wyszukiwanie"
            className="absolute top-1/2 right-2 grid size-9 -translate-y-1/2 place-items-center rounded-full text-white/50 transition hover:bg-white/10 hover:text-white"
          >
            <X size={14} aria-hidden="true" />
          </button>
        ) : null}
      </div>

      {/* ── PAGES ─────────────────────────────────────────── */}
      <div className="mt-3 flex flex-wrap gap-2">
        <FilterChip on={page === null} onClick={() => setPage(null)}>
          Wszystko <Count>{offerings.length}</Count>
        </FilterChip>
        {groups.map((group) => (
          <FilterChip
            key={group.slug}
            on={page === group.slug}
            onClick={() => setPage(page === group.slug ? null : group.slug)}
          >
            {group.label} <Count>{group.count}</Count>
          </FilterChip>
        ))}
      </div>

      {/* ── THE OFFER ─────────────────────────────────────── */}
      {shown.length === 0 ? (
        <p className={cn(META, 'mt-6 text-white/55')}>
          Nic takiego nie mamy w ofercie.{' '}
          <button
            type="button"
            onClick={() => {
              setQuery('')
              setPage(null)
            }}
            className="font-semibold text-white/75 underline underline-offset-4 transition hover:text-white"
          >
            Pokaż wszystko
          </button>
        </p>
      ) : (
        <ul className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {shown.map((offering) => (
            <CatalogueTile
              key={offering.slug}
              offering={offering}
              inOrder={taken.has(offering.slug)}
              // A deep link arrives pointed at the level it named; everything
              // else at the recommended one.
              pick={offering.slug === linked?.offering.slug ? linked.pick : quickPick(offering)}
              open={expanded === offering.slug}
              onToggle={() => setExpanded(expanded === offering.slug ? null : offering.slug)}
              onAdd={(pick) => onAdd(offering.slug, pick)}
            />
          ))}
        </ul>
      )}
    </section>
  )
}

/* ─────────────────────────────────────────────────────────── */

function Count({ children }: { children: React.ReactNode }) {
  return <span className="ml-1 tabular-nums opacity-55">{children}</span>
}

function FilterChip({
  on,
  onClick,
  children,
}: {
  on: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      className={cn(
        'inline-flex min-h-9 items-center border px-3.5 py-1.5 text-[0.8rem] font-semibold transition',
        RADIUS.pill,
        'focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:outline-none',
        on ? PICKED_FILL : 'border-white/15 text-white/65 hover:border-white/35 hover:text-white',
      )}
    >
      {children}
    </button>
  )
}

/**
 * One thing on offer, as a tile you can add in a single press.
 *
 * ## Already in the order, and still on the grid
 *
 * A tile whose offering is in the basket keeps its place and says so, rather
 * than disappearing. Two reasons, and the second is the one that matters:
 * things vanishing out of a grid as you use it makes the grid move under your
 * hand, and somebody ordering four miniaturki as four separate positions — a
 * different brief on each, which is exactly how they are sold — has to be able
 * to press the same tile again.
 */
function CatalogueTile({
  offering,
  pick,
  inOrder,
  open,
  onToggle,
  onAdd,
}: {
  offering: CreatorOfferingView
  pick: CreatorPick
  inOrder: boolean
  open: boolean
  onToggle: () => void
  onAdd: (pick: CreatorPick) => void
}) {
  const price = fromPrice(offering)
  const levels = offering.variants.length

  return (
    <li
      className={cn(
        'flex flex-col gap-3 border border-white/12 bg-white/[0.04] p-4 transition-colors duration-200 hover:border-white/28 hover:bg-white/[0.07]',
        RADIUS.box,
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          {/* The SHELF, which for a hire item says "Światło" where the page
              label would say "Wynajem" for the eleventh time in a row. They
              are the same string for everything else, so this costs nothing
              where there is nothing to gain. */}
          <span className={cn(MICRO, 'font-semibold tracking-[0.14em] text-white/40 uppercase')}>
            {offering.categoryLabel === offering.pageLabel
              ? offering.pageLabel
              : `${offering.pageLabel} · ${offering.categoryLabel}`}
          </span>
          <h3 className="mt-0.5 text-[0.95rem] leading-snug font-bold text-white">
            {offering.name}
          </h3>
        </div>
        {inOrder ? (
          <span
            className={cn(
              MICRO,
              RADIUS.pill,
              'inline-flex shrink-0 items-center gap-1 border border-white/20 px-2 py-0.5 font-semibold text-white/60',
            )}
          >
            <Check size={10} aria-hidden="true" />w zamówieniu
          </span>
        ) : null}
      </div>

      {offering.blurb ? (
        <p className={cn(MICRO, 'line-clamp-2 flex-1 leading-relaxed text-white/55')}>
          {offering.blurb}
        </p>
      ) : null}

      <div className="flex items-end justify-between gap-2">
        <span className="min-w-0">
          {price ? (
            <>
              <span className={cn(MICRO, 'block text-white/40')}>od</span>
              <span className="block text-[1.05rem] leading-none font-bold text-white tabular-nums">
                {price}
              </span>
            </>
          ) : (
            <span className={cn(MICRO, 'block text-white/55')}>wycena indywidualna</span>
          )}
        </span>

        <span className="flex shrink-0 items-center gap-1.5">
          {/* The levels live behind a disclosure rather than on every tile: a
              grid of twenty-five tiles each showing three chips is the
              dropdown's problem again, in a wider shape. */}
          {levels > 1 ? (
            <button
              type="button"
              onClick={onToggle}
              aria-expanded={open}
              aria-label={`Poziomy — ${offering.name}`}
              className={cn(
                'grid size-9 place-items-center rounded-full border border-white/15 text-white/60 transition hover:border-white/35 hover:text-white',
                'focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:outline-none',
              )}
            >
              <ChevronDown
                size={14}
                aria-hidden="true"
                className={cn('transition-transform duration-200', open && 'rotate-180')}
              />
            </button>
          ) : null}

          <button
            type="button"
            onClick={() => onAdd(pick)}
            className={cn(
              'inline-flex min-h-9 items-center gap-1.5 px-3.5 text-[0.8rem] font-semibold text-white transition-transform duration-200 hover:scale-[1.04]',
              RADIUS.pill,
              'bg-[image:var(--flou-gradient-solid)] shadow-[0_10px_26px_-14px_var(--flou-glow-core)]',
              'focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:outline-none',
            )}
          >
            <Plus size={13} strokeWidth={2.6} aria-hidden="true" />
            Dodaj
          </button>
        </span>
      </div>

      {open && levels > 1 ? (
        <div className="flex flex-col gap-2 border-t border-white/10 pt-3">
          <div className="flex flex-wrap gap-2">
            {offering.variants.map((variant) => (
              <ChoiceChip
                key={variant.slug}
                pressed={variant.slug === pick.variantSlug}
                note={`${variant.price}${variant.suffix ? ` ${variant.suffix}` : ''}`}
                onClick={() =>
                  onAdd({
                    ...pick,
                    variantSlug: variant.slug,
                    qty: defaultQtyFor(offering, variant),
                  })
                }
              >
                {variant.name}
              </ChoiceChip>
            ))}
          </div>
          <Link
            href={offering.href}
            className={cn(
              MICRO,
              'self-start font-semibold text-white/50 underline-offset-4 transition hover:text-white hover:underline',
            )}
          >
            Zobacz zakres
          </Link>
        </div>
      ) : null}
    </li>
  )
}
